'use client';

import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Locale } from '@fxtr/i18n/locale';
import { useResizeViewport } from '$util/hooks/useResizeViewport';
import { Button } from '@/components/atoms/Button';
import { VrmLookupDumbModal } from '@/components/molecules/VrmLookupDumbModal';
import { VrmLookupDumb } from '@/components/molecules/VrmLookupDumb';
import type { VrmLookupFormTranslations } from '@/components/molecules/VrmLookupDumb/translations';
import type { VrmLookupModalTranslations } from '@/components/molecules/VrmLookupDumbModal/translations';
import type { MandatoryProp } from '@/types/react';
import { getPageOverviewLink } from '@/app/(landingPages)/(overview)/getPageOverviewLink';

import styles from './index.module.scss';

const stickyButton = (observedTarget: IntersectionObserverEntry, setState: (state: boolean) => void) => {
  const { isIntersecting } = observedTarget;
  if (window.scrollY === 0) {
    setState(false);
  } else {
    setState(!isIntersecting);
  }
};

interface MobileStickyCtaProps {
  readonly locale: Locale;
  readonly sku: MandatoryProp<string>;
  readonly campaignId: MandatoryProp<string>;
  readonly lookupFormTranslations: VrmLookupFormTranslations;
  readonly modalTranslations: VrmLookupModalTranslations;
}

export default function MobileStickyCta({
  locale,
  sku,
  campaignId,
  lookupFormTranslations,
  modalTranslations,
}: MobileStickyCtaProps): ReactNode {
  const [showStickyButton, setShowStickyButton] = useState(false);

  const [stickyButtonIntersectionObserver, setStickyButtonIntersectionObserver] = useState<
    IntersectionObserver | undefined
  >(undefined);

  useEffect(() => {
    const heroVrmTarget = document.querySelector<HTMLElement>('.Hero24VrmForm');
    if (heroVrmTarget) {
      const observer = new IntersectionObserver(
        ([observedTarget]) => stickyButton(observedTarget, setShowStickyButton),
        {
          threshold: [0],
        }
      );

      observer.observe(heroVrmTarget);

      setStickyButtonIntersectionObserver(observer);

      return () => {
        observer.unobserve(heroVrmTarget);
      };
    }
    return () => undefined; // needed due to consistent return lint rule
  }, [setStickyButtonIntersectionObserver, setShowStickyButton]);

  useResizeViewport({
    breakpoint: 'small',
    direction: 'up',
    fn: (isDesktop) => {
      const heroVrmTarget = document.querySelector<HTMLElement>('.Hero24VrmForm');
      if (heroVrmTarget) {
        if (isDesktop) {
          stickyButtonIntersectionObserver?.unobserve(heroVrmTarget);
        } else {
          stickyButtonIntersectionObserver?.observe(heroVrmTarget);
        }
      }
    },
  });

  const mainCtaText = locale === Locale.FR_FR ? 'Obtenir un devis' : 'See our services';

  return (
    showStickyButton &&
    createPortal(
      <div className={styles.stickyCTA}>
        {sku ? (
          <VrmLookupDumbModal
            className={styles.toggleButton}
            size="M"
            triggeredFrom="MobileStickyCta"
            {...modalTranslations}
          >
            <VrmLookupDumb locale={locale} sku={sku} campaignId={campaignId} {...lookupFormTranslations} />
          </VrmLookupDumbModal>
        ) : (
          <Button className={styles.toggleButton} size="M" href={getPageOverviewLink(locale, campaignId)}>
            {mainCtaText}
          </Button>
        )}
      </div>,
      document.body
    )
  );
}
